// Settings → Attendance Kiosk.
//
// A kiosk is ONE sign-in that a whole roster shares: the tablet at the gate,
// the desk in reception. The admin names it, gets a generated address and
// password, ticks the employees who may use it, and hands the credential to
// whoever mans the device. Signing in with it on the messenger app opens the
// attendance panel and nothing else.
//
// The password is shown ONCE — hrms stores an argon2 hash and has no way to
// read it back — so the reveal panel below stays open until the admin
// explicitly dismisses it, and says so in as many words.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  Copy,
  Eye,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Tablet,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import {
  createKiosk,
  deleteKiosk,
  getKioskMembers,
  listKiosks,
  replaceKioskMembers,
  resetKioskPassword,
  updateKiosk,
  type Kiosk,
  type KioskMember,
} from '../../api/attendanceKiosk';
import { getEmployees } from '../../api/employees';
import { useAccess } from '../../contexts/AccessContext';
import { usePermissionGuard } from '../../hooks/usePermissionGuard';
import { Popup } from '../../components/Popup';
import PersonAvatar from '../../components/people/PersonAvatar';

// Access-Management panel key for this page (see config/panelCatalog.ts).
// View = see the kiosks and their rosters; Manage = create, edit, assign.
const PANEL_KEY = 'set-attendance-kiosk';

interface EmployeeRow {
  id: string;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  email?: string | null;
  employeeID?: string | null;
  designation?: string | null;
  // Both come from /employeesAll and both feed the roster row's photo.
  user_id?: string | null;
  profile_photo_url?: string | null;
}

const employeeName = (e: EmployeeRow): string =>
  e.name?.trim() ||
  [e.first_name, e.middle_name, e.last_name].filter(Boolean).join(' ').trim() ||
  e.email ||
  'Unnamed';

const errText = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.message || fallback;
  return err?.message || fallback;
};

/** The one-shot credential reveal. Deliberately not a toast: a toast that
 *  auto-dismisses would take the only copy of the password with it. */
function CredentialPanel({
  email,
  password,
  onDone,
}: {
  email: string;
  password: string;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState<'email' | 'password' | 'both' | null>(null);

  const copy = async (what: 'email' | 'password' | 'both', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard blocked (http, or a browser that refuses) — the value is
         on screen either way, which is the part that matters */
    }
  };

  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 space-y-3">
      <div className="flex items-start gap-2">
        <KeyRound className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-amber-900">Copy these now</div>
          <p className="text-sm text-amber-800">
            The password is shown only this once — it is stored encrypted and cannot be
            read back. If you lose it, use <strong>Reset password</strong> on the kiosk.
          </p>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <div className="rounded-xl bg-white border border-amber-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-500">Kiosk ID</div>
          <div className="font-mono text-sm break-all">{email}</div>
        </div>
        <div className="rounded-xl bg-white border border-amber-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-500">Password</div>
          <div className="font-mono text-sm break-all">{password}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => copy('both', `Kiosk ID: ${email}\nPassword: ${password}`)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-amber-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-800"
        >
          {copied === 'both' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          Copy both
        </button>
        <button
          type="button"
          onClick={() => copy('password', password)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
        >
          {copied === 'password' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          Copy password
        </button>
        <button
          type="button"
          onClick={onDone}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
        >
          I have copied it
        </button>
      </div>
    </div>
  );
}

export default function AttendanceKiosk() {
  const { canManage } = useAccess();
  const { showError, showSuccess, popup, closePopup } = usePermissionGuard();
  const readOnly = !canManage(PANEL_KEY);

  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [members, setMembers] = useState<KioskMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [savingRoster, setSavingRoster] = useState(false);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', username: '', password: '' });
  const [credential, setCredential] = useState<{ email: string; password: string } | null>(null);

  const selected = useMemo(
    () => kiosks.find((k) => k.id === selectedId) || null,
    [kiosks, selectedId],
  );

  const loadKiosks = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listKiosks();
      setKiosks(rows);
      setSelectedId((prev) => (prev && rows.some((k) => k.id === prev) ? prev : rows[0]?.id ?? null));
    } catch (err) {
      showError('Could not load kiosks', errText(err, 'Please try again.'));
    } finally {
      setLoading(false);
    }
    // showError is stable enough for this page; re-running on it would refetch
    // on every popup open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadKiosks();
  }, [loadKiosks]);

  // The employee pick-list. Loaded once — it feeds the roster editor for every
  // kiosk, so refetching per selection would be the same list each time.
  useEffect(() => {
    getEmployees({ active_only: true })
      .then((r: any) => setEmployees(Array.isArray(r?.employees) ? r.employees : []))
      .catch(() => setEmployees([]));
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setMembers([]);
      setPicked(new Set());
      return;
    }
    let cancelled = false;
    setMembersLoading(true);
    getKioskMembers(selectedId)
      .then((rows) => {
        if (cancelled) return;
        setMembers(rows);
        setPicked(new Set(rows.map((m) => m.emptID)));
      })
      .catch(() => {
        if (!cancelled) {
          setMembers([]);
          setPicked(new Set());
        }
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => {
      const hay = `${employeeName(e)} ${e.employeeID ?? ''} ${e.email ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [employees, search]);

  // Dirty check drives the Save button — the roster is a REPLACE call, so
  // saving an unchanged set would be a pointless write.
  const rosterDirty = useMemo(() => {
    const current = new Set(members.map((m) => m.emptID));
    if (current.size !== picked.size) return true;
    for (const id of picked) if (!current.has(id)) return true;
    return false;
  }, [members, picked]);

  const togglePicked = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) {
      showError('Read-only access', 'You have view-only access to Attendance Kiosk.');
      return;
    }
    if (!form.name.trim()) {
      showError('Name required', 'Give the kiosk a name, e.g. "Gate 1 Tablet".');
      return;
    }
    setCreating(true);
    try {
      const created = await createKiosk({
        name: form.name.trim(),
        username: form.username.trim() || undefined,
        password: form.password.trim() || undefined,
      });
      setCredential({ email: created.login_email, password: created.password });
      setForm({ name: '', username: '', password: '' });
      await loadKiosks();
      setSelectedId(created.id);
    } catch (err) {
      showError('Could not create kiosk', errText(err, 'Please try again.'));
    } finally {
      setCreating(false);
    }
  };

  const handleSaveRoster = async () => {
    if (!selected || readOnly) return;
    setSavingRoster(true);
    try {
      const rows = await replaceKioskMembers(selected.id, Array.from(picked));
      setMembers(rows);
      setPicked(new Set(rows.map((m) => m.emptID)));
      await loadKiosks();   // member_count on the card
      showSuccess('Saved', `${rows.length} employee${rows.length === 1 ? '' : 's'} on this kiosk.`);
    } catch (err) {
      showError('Could not save the list', errText(err, 'Please try again.'));
    } finally {
      setSavingRoster(false);
    }
  };

  const handleToggleActive = async (kiosk: Kiosk) => {
    if (readOnly) return;
    try {
      await updateKiosk(kiosk.id, { is_active: !kiosk.is_active });
      await loadKiosks();
    } catch (err) {
      showError('Could not update kiosk', errText(err, 'Please try again.'));
    }
  };

  const handleChangeId = async (kiosk: Kiosk) => {
    if (readOnly) return;
    // eslint-disable-next-line no-alert
    const typed = window.prompt(
      'New sign-in ID for this kiosk.\n\nType a full email address to use it exactly, '
        + 'or a plain username to get one on the kiosk domain.',
      kiosk.login_email,
    );
    if (typed === null || !typed.trim()) return;
    try {
      await updateKiosk(kiosk.id, { username: typed.trim() });
      await loadKiosks();
      showSuccess('Sign-in ID changed', 'Devices already signed in keep working.');
    } catch (err) {
      showError('Could not change the ID', errText(err, 'Please try again.'));
    }
  };

  const handleResetPassword = async (kiosk: Kiosk) => {
    if (readOnly) return;
    try {
      const res = await resetKioskPassword(kiosk.id);
      setCredential({ email: res.login_email, password: res.password });
    } catch (err) {
      showError('Could not reset the password', errText(err, 'Please try again.'));
    }
  };

  const handleDelete = async (kiosk: Kiosk) => {
    if (readOnly) return;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete "${kiosk.name}"? Anyone signed in on it will be locked out.`)) return;
    try {
      await deleteKiosk(kiosk.id);
      setSelectedId(null);
      await loadKiosks();
    } catch (err) {
      showError('Could not delete kiosk', errText(err, 'Please try again.'));
    }
  };

  return (
    <div className="min-h-full bg-[#faf8f3]">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Tablet className="h-6 w-6 text-emerald-700" />
          <h1 className="text-xl font-semibold">Attendance Kiosk</h1>
          {readOnly && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold px-2.5 py-1">
              <Eye className="h-3.5 w-3.5" /> View only
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 -mt-4">
          One shared sign-in for a device several people mark attendance on. Signing in with
          it on the messenger app opens the attendance panel and nothing else.
        </p>

        {credential && (
          <CredentialPanel
            email={credential.email}
            password={credential.password}
            onDone={() => setCredential(null)}
          />
        )}

        {/* Create */}
        {!readOnly && (
          <form
            onSubmit={handleCreate}
            className="bg-white border border-[#e8e3d8] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,45,31,0.04)] space-y-4"
          >
            <div className="font-semibold">New kiosk</div>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="text-sm">
                <span className="block text-gray-600 mb-1">Name</span>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Gate 1 Tablet"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </label>
              <label className="text-sm">
                <span className="block text-gray-600 mb-1">Username (optional)</span>
                <input
                  value={form.username}
                  onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                  placeholder="gate-1"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
                <span className="block text-[11px] text-gray-500 mt-1">
                  Type a full email to use it exactly, or a plain word to get one on
                  the kiosk domain. Blank uses the name.
                </span>
              </label>
              <label className="text-sm">
                <span className="block text-gray-600 mb-1">Password (optional)</span>
                <input
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Leave blank to generate"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-60"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create kiosk
            </button>
          </form>
        )}

        {/* Kiosk list */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading kiosks…
            </div>
          ) : kiosks.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
              No kiosks yet. Create one above to get a shared sign-in.
            </div>
          ) : (
            kiosks.map((k) => {
              const isSelected = k.id === selectedId;
              return (
                <div
                  key={k.id}
                  className={`bg-white border rounded-2xl p-4 ${
                    isSelected ? 'border-emerald-500 ring-1 ring-emerald-200' : 'border-[#e8e3d8]'
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedId(isSelected ? null : k.id)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{k.name}</span>
                        {!k.is_active && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                            Switched off
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-xs text-gray-500 break-all">{k.login_email}</div>
                      <div className="mt-1 inline-flex items-center gap-1 text-xs text-gray-600">
                        <Users className="h-3.5 w-3.5" />
                        {k.member_count} employee{k.member_count === 1 ? '' : 's'}
                      </div>
                    </button>

                    {!readOnly && (
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleToggleActive(k)}
                          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
                        >
                          {k.is_active ? 'Switch off' : 'Switch on'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleChangeId(k)}
                          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
                        >
                          Change ID
                        </button>
                        <button
                          type="button"
                          onClick={() => handleResetPassword(k)}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
                        >
                          <RefreshCw className="h-3.5 w-3.5" /> Reset password
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(k)}
                          className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </div>
                    )}
                  </div>

                  {isSelected && (
                    <div className="mt-4 border-t border-gray-100 pt-4 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-sm">Employees on this kiosk</div>
                        {membersLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                      </div>

                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search employees…"
                          className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm"
                        />
                      </div>

                      <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100">
                        {filteredEmployees.length === 0 ? (
                          <div className="p-4 text-sm text-gray-500">No employees match that search.</div>
                        ) : (
                          filteredEmployees.map((emp) => (
                            <label
                              key={emp.id}
                              className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded text-emerald-600"
                                disabled={readOnly}
                                checked={picked.has(emp.id)}
                                onChange={() => togglePicked(emp.id)}
                              />
                              <PersonAvatar
                                userId={emp.user_id}
                                employeeId={emp.id}
                                employeeCode={emp.employeeID}
                                photoUrl={emp.profile_photo_url}
                                name={employeeName(emp)}
                                size={28}
                              />
                              <span className="flex-1 min-w-0">
                                <span className="block truncate font-medium">{employeeName(emp)}</span>
                                <span className="block truncate text-xs text-gray-500">
                                  {[emp.employeeID, emp.designation].filter(Boolean).join(' · ') || emp.email}
                                </span>
                              </span>
                            </label>
                          ))
                        )}
                      </div>

                      {!readOnly && (
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={handleSaveRoster}
                            disabled={!rosterDirty || savingRoster}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
                          >
                            {savingRoster ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                            Save list
                          </button>
                          {rosterDirty && (
                            <button
                              type="button"
                              onClick={() => setPicked(new Set(members.map((m) => m.emptID)))}
                              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                            >
                              <X className="h-4 w-4" /> Discard changes
                            </button>
                          )}
                          <span className="text-xs text-gray-500">
                            {picked.size} selected
                          </span>
                        </div>
                      )}

                      <p className="text-xs text-gray-500">
                        An employee belongs to one kiosk. Ticking someone who is already on
                        another kiosk moves them here.
                      </p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <Popup
        isOpen={popup.isOpen}
        onClose={closePopup}
        type={popup.type}
        title={popup.title}
        message={popup.message}
      />
    </div>
  );
}
